import { useRef, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { SbSharedMarketTrendsPriceStats } from './SbSharedMarketTrendsPriceStats';
import { SbSharedMarketTrendsTopSales } from './SbSharedMarketTrendsTopSales';
import { FILTER_NAME_CREATOR } from '../../constants/sbConstants';
import { getTopicDescription } from './languageHelpers';
import { getAppliedFilterDisplayName } from '../helpers/appliedFilters/getAppliedFilterDisplayName';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { trackMarketTrendsCreatorBrowse } from '../../utils/abTestHelper';
import { trackEvent, eventNameConstants } from 'dibs-tracking';
import { pageTypeConstants } from '../../constants/pageTypeConstants';

import dibsCss from 'dibs-css';
import cn from 'classnames';

import { type SbSharedMarketTrends_viewer$key } from './__generated__/SbSharedMarketTrends_viewer.graphql';
import { type SbSharedMarketTrends_itemSearch$key } from './__generated__/SbSharedMarketTrends_itemSearch.graphql';

const viewerFragment = graphql`
    fragment SbSharedMarketTrends_viewer on Viewer {
        creatorMarketTrends(uriRef: $uriRef, first: 5) @include(if: $fetchSb) {
            ...SbSharedMarketTrendsPriceStats_creatorMarketTrends
            ...SbSharedMarketTrendsTopSales_creatorMarketTrends
            priceStats {
                __typename
            }
            edges {
                __typename
            }
            topicDescription {
                categoryName
                hasArticle
            }
        }
    }
`;

const itemSearchFragment = graphql`
    fragment SbSharedMarketTrends_itemSearch on ItemSearchQueryConnection {
        ...getAppliedFilterDisplayName_itemSearch
        pageType
    }
`;

type Props = {
    viewer: SbSharedMarketTrends_viewer$key;
    itemSearch: SbSharedMarketTrends_itemSearch$key;
};

export const SbSharedMarketTrends: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
}) => {
    const marketTrendsRef = useRef<HTMLDivElement>(null);
    const { isDesktop } = useServerVarsContext();
    const { creatorMarketTrends } = useFragment(viewerFragment, viewerRef) || {};
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);

    if (itemSearch.pageType !== pageTypeConstants.CREATOR) {
        return null;
    }

    const { edges, priceStats, topicDescription } = creatorMarketTrends || {};

    const creatorDisplayName = getAppliedFilterDisplayName(itemSearch, FILTER_NAME_CREATOR) ?? '';
    const { categoryName, hasArticle } = topicDescription || {};
    const topic = getTopicDescription({ creatorDisplayName, categoryName, hasArticle });

    return (
        <>
            <VisibilityTracker
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        // Track AB test when user scrolls past pagination
                        trackMarketTrendsCreatorBrowse();
                    }
                }}
            />
            <VisibilityTracker
                elementRef={marketTrendsRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        // Track actual user view of market trends event
                        trackEvent({
                            eventName: eventNameConstants.EVENT_BROWSE_INTERACTION,
                            interaction_type: 'market trends - view',
                        });
                    }
                }}
            />
            {!!(creatorMarketTrends && (priceStats || edges?.length)) && (
                <article
                    ref={marketTrendsRef}
                    className={cn(
                        dibsCss.textNoir,
                        dibsCss.borderDolphin,
                        ...(isDesktop
                            ? [
                                  dibsCss.myLarger,
                                  dibsCss.pLarger,
                                  dibsCss.border,
                                  dibsCss.borderSolid,
                              ]
                            : [
                                  dibsCss.myLarge,
                                  dibsCss.pyMedium,
                                  dibsCss.borderY,
                                  dibsCss.borderYSolid,
                              ])
                    )}
                >
                    <HeadingLevel>
                        {Heading => (
                            <>
                                <Heading
                                    className={cn(
                                        dibsCss.sassyFontHeaderLargeNormal,
                                        dibsCss.mt0,
                                        isDesktop ? dibsCss.mbSmall : dibsCss.mbMedium
                                    )}
                                >
                                    <FormattedMessage
                                        id="abf.SbSharedMarketTrends.header"
                                        defaultMessage="Market Trends"
                                    />
                                </Heading>

                                <SbSharedMarketTrendsPriceStats
                                    creatorMarketTrends={creatorMarketTrends}
                                    topic={topic}
                                />

                                <SbSharedMarketTrendsTopSales
                                    creatorMarketTrends={creatorMarketTrends}
                                    topic={topic}
                                />
                            </>
                        )}
                    </HeadingLevel>
                </article>
            )}
        </>
    );
};
